import React, { useState, useEffect, useMemo } from "react";
import Grid from "@mui/material/Grid2";
import { TextField, Container, Alert } from "@mui/material";
import { getcampaigns } from "../../services/api";
import { fetchCustomersInfo, inEmailCustomer, inTextCustomer } from "../../services/customerModule/customers/customerServices.js";
import { getComparator, handleSort } from "../../hooks/useTable.js";
import { AUTO_COMPLETE, VARIANTS, SORT_ORDERS, SIZES, STATUS } from "../../common/constants/commonConstants.js";
import { CUSTOMER_STRINGS, CUSTOMER_KEYS, CUSTOMER_ERRORS, CUSTOMER_MESSAGES, CUSTOMER_TABLE_COLUMNS } from "../../common/constants/customerModule/customerConstants.js";
import CustomersConvTable from "./CustomersConvTable.jsx";
import TextCustomerDialog from "./textCustomerDialog.jsx";
import EmailCustomerDialog from "./emailCustomerDialog.jsx";
//import CustomerInfo from "./CustomerInfo";

const CustomersConv = () => {
    const [page, setPage] = useState(0);
    const [customers, setCustomers] = useState([]);
    const [campaigns, setCampaigns] = useState([]);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchCustomer, setSearchCustomer] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const [order, setOrder] = useState(SORT_ORDERS.ASCENDING);
    const [orderBy, setOrderBy] = useState(CUSTOMER_KEYS.NAME);
    const [loadingCustomers, setLoadingCustomers] = useState(true);
    const [loadingCampaigns, setLoadingCampaigns] = useState(true);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [isTextCustomerModalOpen, setIsTextCustomerModalOpen] = useState(false);
    const [isEmailCustomerModalOpen, setIsEmailCustomerModalOpen] = useState(false);
    //const [viewCustomerGuid, setViewCustomerGuid] = useState(null);

    useEffect(() => { 
        loadCustomers(); 
        loadCampaigns(); 
    }, []);

    const loadCustomers = async () => {
        try {
            setLoadingCustomers(true);
            setCustomers(await fetchCustomersInfo());
        }
        catch (error) {
            console.error(CUSTOMER_ERRORS.FETCH_CUSTOMERS_ERROR, error);
        }
        finally {
            setLoadingCustomers(false);
        }
    };

    const loadCampaigns = async () => {
        try {
            setLoadingCampaigns(true);
            let data = await getcampaigns();
            data = data.sort((a, b) => a.id - b.id);
            setCampaigns(data);
        }
        catch (error) {
            console.error("Error fetching campaigns:", error);
        }
        finally {
            setLoadingCampaigns(false);
        }
    };

    const customersList = customers.map((customer) => ({
        ...customer,
        name: `${customer.firstname ?? ""} ${customer.lastname ?? ""}`.trim(),
    }));

    const { customersToDisplay, filteredCustomersLength } = useMemo(() => {
        const search = searchCustomer.toLowerCase().trim();

        const filteredCustomers = customersList.filter((customer) => {
            const customerId = customer.customerid?.toString().toLowerCase() || "";
            const firstName = customer.firstname?.toLowerCase() || "";
            const lastName = customer.lastname?.toLowerCase() || "";

            return (
                customerId.includes(search) ||
                firstName.includes(search) ||
                lastName.includes(search)
            );
        });

        return {
            customersToDisplay: filteredCustomers
                .sort(getComparator(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
            filteredCustomersLength: filteredCustomers.length,
        };
    }, [customersList, searchCustomer, order, orderBy, page, rowsPerPage]);

    const handleTextCustomerClick = (customer = {}) => {
        setSelectedCustomer(customer);
        setIsTextCustomerModalOpen(true);
    };

    const handleEmailCustomerClick = (customer = {}) => {
        setSelectedCustomer(customer);
        setIsEmailCustomerModalOpen(true);
    };

    const handleEmailSaveCustomer = async () => {
        if (!selectedCustomer) return;
        try {
            if (selectedCustomer.id) {
                await inEmailCustomer(selectedCustomer.id);
                setSuccessMessage(CUSTOMER_MESSAGES.CUSTOMER_EMAIL);
            } else {
                setSuccessMessage(CUSTOMER_MESSAGES.CUSTOMER_EMAIL);
            }
            setIsEmailCustomerModalOpen(false);
            await loadCustomers();
            setTimeout(() => setSuccessMessage(""), 3000);
        } catch (error) {
            console.error(CUSTOMER_ERRORS.EMAIL_CUSTOMERS_ERROR, error);
        }
    };

    const handleTextSaveCustomer = async () => {
        //console.log('Got to Handle');
        if (!selectedCustomer) 
            return;
        try {
            if (selectedCustomer.id) {
                //console.log('Send message');
                await inTextCustomer(selectedCustomer.id);
                //console.log('Send message II');
                setSuccessMessage(CUSTOMER_MESSAGES.CUSTOMER_TEXT);
            } else {
                setSuccessMessage(CUSTOMER_MESSAGES.CUSTOMER_TEXT);
            }
            setIsTextCustomerModalOpen(false);
            await loadCustomers();
            setTimeout(() => setSuccessMessage(""), 3000);
        } catch (error) {
            console.error(CUSTOMER_ERRORS.TEXT_CUSTOMERS_ERROR, error);
        }
    };

    const handleInputChange = (field) => (e) =>
        setSelectedCustomer({ ...selectedCustomer, [field]: e.target.value });

    const handleRequestSort = (property) => {
        handleSort(order, orderBy, property, setOrder, setOrderBy);
    };

    /* const handleViewCustomer = (customer) => {
        setViewCustomerGuid(customer.customerguid);
    }; */

    /* if (viewCustomerGuid) {
        return <CustomerInfo customerguid={viewCustomerGuid} onBack={() => setViewCustomerGuid(null)} />;
    } */

    return (
        <>
            <Container width="xl" disableGutters>
                {successMessage && (
                    <Alert severity={STATUS.SUCCESS} sx={{ mb: 2, justifyContent: "center" }}>
                        {successMessage}
                    </Alert>
                )}
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid size={SIZES.GROW}>
                        <TextField
                            fullWidth
                            size={SIZES.SMALL}
                            value={searchCustomer}
                            variant={VARIANTS.OUTLINED}
                            autoComplete={AUTO_COMPLETE.OFF}
                            label={CUSTOMER_STRINGS.SEARCH_CUSTOMERS}
                            onChange={e => setSearchCustomer(e.target.value)}
                        />
                    </Grid>
{/*                     <Grid size={SIZES.AUTO}>
                        <Button
                            startIcon={<Add />}
                            variant={VARIANTS.CONTAINED}
                            color={BUTTON_COLORS.PRIMARY}
                            onClick={() => handleAddEditCustomerClick({})}
                        >
                            {CUSTOMER_STRINGS.ADD_CUSTOMER}
                        </Button>
                    </Grid> */}
                </Grid>
                <CustomersConvTable
                    page={page}
                    order={order}
                    setPage={setPage}
                    orderBy={orderBy}
                    campaigns={campaigns}
                    rowsPerPage={rowsPerPage}
                    customers={customersToDisplay}
                    setRowsPerPage={setRowsPerPage}
                    columns={CUSTOMER_TABLE_COLUMNS}
                    onEmail={handleEmailCustomerClick}
                    onText={handleTextCustomerClick}
                    handleRequestSort={handleRequestSort}
                    isCustomersLoading={loadingCustomers}
                    isCampaignsLoading={loadingCampaigns}
                    filteredCustomersLength={filteredCustomersLength}
                />
                <EmailCustomerDialog
                    onEmail={handleEmailSaveCustomer}
                    open={isEmailCustomerModalOpen}
                    selectedCustomer={selectedCustomer}
                    handleInputChange={handleInputChange}
                    onClose={() => setIsEmailCustomerModalOpen(false)}
                />
                <TextCustomerDialog
                    onText={handleTextSaveCustomer}
                    open={isTextCustomerModalOpen}
                    selectedCustomer={selectedCustomer}
                    handleInputChange={handleInputChange}
                    onClose={() => setIsTextCustomerModalOpen(false)}
                />
            </Container>
        </>
    )
};

export default CustomersConv;